import { Layer, LayerManager, PickingInfo } from '@deck.gl/core';
import { MapboxOverlay as DeckOverlay } from '@deck.gl/mapbox';
import { MonitorStatus } from '@fe/core';
import { FeMapPlugin } from '@fe/maplibre-extensions';
import { Parameters, Texture } from '@luma.gl/core';
import { Map as MaplibreMap, MapMovementEvent } from 'maplibre-gl';
import FlightItem from '../flights/flight-item';
import { FlightsUpdater } from '../flights/flights-updater';
import { IFlightItem } from '../flights/types';
import { ParkedFlight, ParkedUpdater } from '../parked/parked-updater';
import { ScheduledUpdater } from '../scheduled/scheduled-updater';
import { Selection } from '../selection';
import { AutoArrange } from './auto-arrange';
import { DeckActiveFlights } from './deck-active-flights';
import { CityPairItem, deckCityPairLayer } from './deck-city-pair';
import { DeckCoast } from './deck-coast';
import { DeckDiversions } from './deck-diversions';
import { DeckFlightForward } from './deck-flight-forward';
import { DeckFlightHistory } from './deck-flight-history';
import { DeckMarkers } from './deck-markers';
import { DeckMonitorStatus } from './deck-monitor-status';
import { DeckParked } from './deck-parked';
import { DeckParticles } from './deck-particles';
import { DeckRings } from './deck-rings';
import { DeckRouteFlownLastSegment } from './deck-route-flown-last-segment';
import { DeckScheduledPlanned } from './deck-scheduled-planned';
import { DeckTags } from './deck-tags';
import { DeckVectors } from './deck-vectors';
import { FlightIconLayerProps } from './layers/flight/flight-icon-layer';
import { HidableTextLayer } from './layers/text-tag/hiddable-text-layer';
import { DeckOverlayProps, DepartureQueryResult, FlightForwardFlight, FlightForwardProps, TagLeaderItem } from './types';

interface FlightData {
    tagData: IFlightItem[];
    vectorData: IFlightItem[];
    ringData: IFlightItem[];
    historyData: IFlightItem[];
    lastRouteFlownSegment: IFlightItem[];
    markerData: IFlightItem[];
    monitorStatusData: IFlightItem[];
    diversionData: IFlightItem[];
}

export class DeckOverlayPlugin implements FeMapPlugin {
    readonly flightsUpdater: FlightsUpdater;
    readonly selection: Selection;
    readonly parkedUpdater: ParkedUpdater;
    readonly scheduledUpdater: ScheduledUpdater;
    readonly autoArrange: AutoArrange;
    readonly workspaceId: number;

    private coast: DeckCoast | null = null;
    private activeFlights: DeckActiveFlights | null = null;
    private monitorStatus: DeckMonitorStatus | null = null;
    private rings: DeckRings | null = null;
    private markers: DeckMarkers | null = null;
    private vectors: DeckVectors | null = null;
    private tags: DeckTags | null = null;
    private diversions: DeckDiversions | null = null;
    private routeFlownLastSegment: DeckRouteFlownLastSegment | null = null;

    private particles: DeckParticles | null = null;
    private history: DeckFlightHistory | null = null;
    private scheduled: DeckScheduledPlanned | null = null;
    private parked: DeckParked | null = null;
    private flightForward: DeckFlightForward | null = null;

    private _map: MaplibreMap | null = null;
    private _overlay: DeckOverlay | null = null;
    private _props: DeckOverlayProps | null = null;
    private _propsVersion = 0;
    private _selectedFlights: IFlightItem[] | null = null;
    private _tagZoomThresholdReached: boolean = false;
    private _positionTexture: Texture | null = null;

    // Extension point that lets other plugins (e.g. CustomGeojson3d) contribute deck.gl layers into the single shared MapboxOverlay instance
    private readonly externalLayerGroups = new Map<string, Layer[]>();
    private _coreLayers: Layer[] = [];
    private _onHover?: (info: PickingInfo) => void;
    private _getCursor?: (params: { isDragging: boolean }) => string;

    constructor(
        workspaceId: number,
        flightUpdater: FlightsUpdater,
        selection: Selection,
        parked: ParkedUpdater,
        scheduled: ScheduledUpdater
    ) {
        this.workspaceId = workspaceId;
        this.flightsUpdater = flightUpdater;
        this.selection = selection;
        this.parkedUpdater = parked;
        this.scheduledUpdater = scheduled;
        this.autoArrange = new AutoArrange(this);

        this.onZoomEnd = this.onZoomEnd.bind(this);
        this.onMoveEnd = this.onMoveEnd.bind(this);
    }

    get map(): MaplibreMap | null {
        return this._map;
    }

    get overlay(): DeckOverlay | null {
        return this._overlay;
    }

    get flightsVersion(): number {
        return this.flightsUpdater.version;
    }

    get isCoastMode(): boolean {
        return !!this.props?.flights.active.isCoastMode;
    }

    get selectedFlights(): IFlightItem[] | null {
        return this._selectedFlights;
    }

    get props(): DeckOverlayProps | null {
        return this._props;
    }

    get propsVersion(): number {
        return this._propsVersion;
    }

    set props(value: DeckOverlayProps) {
        const { minZoom } = value.flights.active.tags;
        if (this._props?.flights.active.tags.minZoom !== minZoom) {
            this._tagZoomThresholdReached = (this.map?.getZoom() ?? 0) >= minZoom;
        }
        this._props = value;
        this._propsVersion++;
    }

    set flightForwardProps(value: FlightForwardProps) {
        if (this.flightForward) {
            this.flightForward.flightForwardProps = value;
        }
    }

    set flightForwardFlight(value: FlightForwardFlight | null) {
        if (this.flightForward) {
            this.flightForward.flight = value;
        }
    }

    deckCoastDebugProps = {
        coastTargetMode: 0,
        maxProjectForwardTimeSeconds: 300,
        minToTargetJump: 0.001,
        maxToTargetJump: 0.15
    };

    planeSdfParams: FlightIconLayerProps<unknown>['sdfParams'] = {
        cutoff: 0.5,
        softEnabled: true,
        softEdgeMin: 0.0,
        softEdgeMax: 0.5,
        outlineEnabled: true,
        outlineMin0: 0.0,
        outlineMin1: 0.0,
        outlineMax0: 0.0,
        outlineMax1: 1.0
    };

    cityPairData: CityPairItem[] = [];

    get flightForwardFlight(): FlightForwardFlight | null {
        return this.flightForward?.flight ?? null;
    }

    get positionTexture(): Texture | null {
        return this._positionTexture;
    }

    setPositionTexture(texture: Texture): void {
        this._positionTexture = texture;
    }

    onAdd(map: MaplibreMap): void {
        if (this._overlay) {
            return;
        }
        const overlay = new DeckOverlay({
            pickingRadius: 7
        });

        map.addControl(overlay);
        this._overlay = overlay;
        this._map = map;
        this._map.on('zoomend', this.onZoomEnd);
        this._map.on('moveend', this.onMoveEnd);
        this._tagZoomThresholdReached = this._map.getZoom() >= (this.props?.flights.active.tags.minZoom ?? 0);

        this.coast = new DeckCoast(this);
        this.routeFlownLastSegment = new DeckRouteFlownLastSegment(this);
        this.monitorStatus = new DeckMonitorStatus(this);
        this.diversions = new DeckDiversions(this);
        this.particles = new DeckParticles(this);
        this.rings = new DeckRings(this);
        this.markers = new DeckMarkers(this);
        this.vectors = new DeckVectors(this);
        this.history = new DeckFlightHistory(this);
        this.tags = new DeckTags(this);
        this.activeFlights = new DeckActiveFlights(this);
        this.scheduled = new DeckScheduledPlanned(this);
        this.parked = new DeckParked(this);
        this.flightForward = new DeckFlightForward(this);

        this.applyLayers();
        this.applyPickingHandlers();
    }

    onRemove(): void {
        if (!this.map || !this.overlay) {
            return;
        }
        const deckCanvas = this.overlay.getCanvas();
        this.map.removeControl(this.overlay);

        // TODO: bug in deck.gl
        // removeControl should free all resources, but for some reason deckgl webgl context is not released (maybe a bug in deckgl?)
        // so we free it manually to prevent hitting the context limit in case of multiple map/overlay creations.
        const gl = deckCanvas?.getContext('webgl2') ?? deckCanvas?.getContext('webgl');
        gl?.getExtension('WEBGL_lose_context')?.loseContext();

        this.map.off('zoomend', this.onZoomEnd);
        this.map.off('moveend', this.onMoveEnd);
        this._map = null;
        this._overlay = null;
        this._selectedFlights = null;

        this.coast = null;
        this.routeFlownLastSegment = null;
        this.monitorStatus = null;
        this.diversions = null;
        this.particles = null;
        this.rings = null;
        this.markers = null;
        this.vectors = null;
        this.history = null;
        this.tags = null;
        this.activeFlights = null;
        this.scheduled = null;
        this.parked = null;
        this._positionTexture = null;
    }

    updateLayers(options?: { shouldArrangeTags?: boolean }): void {
        if (!this.props || !this._map) {
            return;
        }

        this._coreLayers = [
            ...this.particles!.createLayers(),
            ...this.configureFlights(options),
            ...this.scheduled!.createLayers(),
            ...this.parked!.createLayers(),
            ...this.flightForward!.createLayers()
        ];

        if (this.props.cityPair?.show) {
            this._coreLayers.push(...deckCityPairLayer(this.cityPairData, this.props.cityPair));
        }

        this.applyLayers();
    }

    registerLayerGroup(id: string, layers: Layer[]): void {
        this.externalLayerGroups.set(id, layers);
        this.applyLayers();
    }

    unregisterLayerGroup(id: string): void {
        if (this.externalLayerGroups.delete(id)) {
            this.applyLayers();
        }
    }

    setPickingHandlers(handlers: { onHover?: (info: PickingInfo) => void; getCursor?: (params: { isDragging: boolean }) => string }): void {
        this._onHover = handlers.onHover;
        this._getCursor = handlers.getCursor;
        this.applyPickingHandlers();
    }

    private applyLayers(): void {
        if (!this._overlay) {
            return;
        }
        const externalLayers = Array.from(this.externalLayerGroups.values()).flat();
        this._overlay.setProps({ layers: [...this._coreLayers, ...externalLayers], _animate: this.isCoastMode });
    }

    private applyPickingHandlers(): void {
        if (!this._overlay) {
            return;
        }
        this._overlay.setProps({ onHover: this._onHover, getCursor: this._getCursor });
    }

    private configureFlights(options?: { shouldArrangeTags?: boolean }): Layer[] {
        const rawData = this.flightsUpdater.data;
        if (rawData.length === 0 || !this._props?.flights.visible) {
            return [];
        }

        // Get data partition for each layer
        const data = this.dispatchFlightData(rawData);
        this._selectedFlights = this.flightsUpdater.getSelectedFlights();
        let leaderData: TagLeaderItem[] | undefined = undefined;

        // TODO: make leader lines be a sublayer in composite TagLayer
        if (options?.shouldArrangeTags) {
            if (this.props?.flights.active.tags.autoTag && !this.props.flights.active.tags.hideLeader) {
                // Cheat a bit to get the current tag layer instance
                // TODO: we need this layer instance inside arrange method to calculate the width of the tag boxes.
                // In the future when we run auto arrange inside a sub layer of tag layer we'll have access to everything we need.
                const tagLayer = ((this.overlay as any)._deck.layerManager as LayerManager).layers.find(
                    (x) => x.id === 'tags' || x.id === 'animated-tags'
                ) as HidableTextLayer | undefined;
                if (tagLayer?.isLoaded) {
                    leaderData = this.autoArrange.arrange(data.tagData, this.map!, tagLayer.getTextBounds.bind(tagLayer));
                }
            }
        }

        const flightLayers: Layer[] = [
            ...this.coast!.createLayers(rawData),
            ...this.routeFlownLastSegment!.createLayers(data.lastRouteFlownSegment),
            ...this.monitorStatus!.createLayers(data.monitorStatusData),
            ...this.diversions!.createLayers(data.diversionData),
            ...this.rings!.createLayers(data.ringData),
            ...this.markers!.createLayers(data.markerData),
            ...this.vectors!.createLayers(data.vectorData),
            ...this.history!.createLayers(data.historyData),
            ...this.tags!.createLayers(data.tagData, leaderData),
            ...this.activeFlights!.createLayers(rawData)
        ];

        return flightLayers;
    }

    private dispatchFlightData(items: IFlightItem[]): FlightData {
        const tagData: IFlightItem[] = [];
        const vectorData: IFlightItem[] = [];
        const ringData: IFlightItem[] = [];
        const historyData: IFlightItem[] = [];
        const markerData: IFlightItem[] = [];
        const monitorStatusData: IFlightItem[] = [];
        const diversionData: IFlightItem[] = [];
        const lastRouteFlownSegment: IFlightItem[] = [];

        const { vectors, rings, showDiversions } = this.props!.flights.active;

        for (const item of items) {
            if (!this.shouldHideTag(item)) {
                tagData.push(item);
            }

            if (item.hasIndividualVector || vectors.isVisible) {
                vectorData.push(item);
            }

            if (item.hasIndividualRing || rings.isVisible) {
                ringData.push(item);
            }

            if (item.hasIndividualHistory || item.filterHistory) {
                historyData.push(item);
            }

            if (item.alertState?.marker) {
                markerData.push(item);
            }

            if (item.monitorStatus >= MonitorStatus.info) {
                monitorStatusData.push(item);
            }

            if (item.isDiverting && showDiversions) {
                diversionData.push(item);
            }

            if (this.props?.flights.active.routeFlownEnabledFlights.includes(item.id) || this.selection.isSelected(item.id)) {
                lastRouteFlownSegment.push(item);
            }
        }

        return {
            tagData,
            vectorData,
            ringData,
            historyData,
            lastRouteFlownSegment,
            markerData,
            monitorStatusData,
            diversionData
        };
    }

    // TODO: combine all queryXXX method into one to save time
    queryFlights(point: [number, number]): number[] {
        const objects = this._overlay?.pickMultipleObjects({
            x: point[0],
            y: point[1],
            radius: 7,
            depth: 5,
            layerIds: ['flights', 'animated-flights']
        });
        return (objects ?? []).map((x) => (x.object as FlightItem).id);
    }

    queryPreDepartureFlights(point: [number, number]): DepartureQueryResult {
        const objects =
            this.overlay?.pickMultipleObjects({
                x: point[0],
                y: point[1],
                radius: 0,
                depth: 5,
                layerIds: ['scheduled-flights', 'planned-flights']
            }) ?? [];

        const scheduled = objects
            .filter((x) => x.layer?.id === 'scheduled-flights')
            .map((x) => ({
                items: [...x.object._items.values()].filter((x) => x.status === 7),
                airport: x.object._name
            }));

        const planned = objects
            .filter((x) => x.layer?.id === 'planned-flights')
            .map((x) => ({
                items: [...x.object._items.values()].filter((x) => x.status === 6),
                airport: x.object._name
            }));

        return {
            scheduled,
            planned
        };
    }

    queryParkedFlights(point: [number, number]): ParkedFlight[] {
        const objects = this.overlay?.pickMultipleObjects({
            x: point[0],
            y: point[1],
            radius: 0,
            depth: 5,
            layerIds: ['parked-flights']
        });

        return objects?.map((x) => x.object) ?? [];
    }

    queryCityPair(point: [number, number]): CityPairItem[] {
        const objects = this.overlay?.pickMultipleObjects({
            x: point[0],
            y: point[1],
            radius: 7,
            depth: 5,
            layerIds: ['city-pair']
        });

        return objects?.map((x) => x.object) ?? [];
    }

    getRenderingParams(defaultParam?: Parameters): Parameters | undefined {
        const param = defaultParam ?? {
            cullMode: 'none'
        };

        return this.props?.globeView.useGlobeView ? param : undefined;
    }

    getCanvas(): HTMLCanvasElement | null {
        return this.overlay?.getCanvas() ?? null;
    }

    private onZoomEnd(e: MapMovementEvent) {
        this._tagZoomThresholdReached = e.target.getZoom() >= (this.props?.flights.active.tags.minZoom ?? 0);
        this.updateLayers({ shouldArrangeTags: true });
    }

    private onMoveEnd() {
        this.updateLayers({ shouldArrangeTags: true });
    }

    // Return number instead of boolean as it is needed by deck.gl isHidden property
    private shouldHideTag(item: IFlightItem): number {
        // No props - hide tags
        if (!this.props || !this.map) {
            return 1;
        }

        // Highlighted plane's tag should always be visible
        if (this.selection.isSelected(item.id) && this.props.flights.active.tags.hasFlightDetailsTag) {
            return 0;
        }

        return (this._tagZoomThresholdReached && item.filterTag) || item.alertState?.tag || item.hasIndividualTag ? 0 : 1;
    }
}
